import AccessCredentialHelp from './AccessCredentialHelp';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { AddCircle, VpnKey } from '@mui/icons-material';
import { Chip, IconButton, Stack, Typography } from '@mui/material';

import { useApiKeys } from '@@common';

import { ApiKeyModel } from '../../../api/api';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';
import CreateApiKeyDialog from './CreateApiKeyDialog';

interface ApiKeysPanelProps {
	onClose: () => void;
	onApiKeySelect: (apiKeyId: string) => void;
	selectedApiKeyId?: string | null;
	onCollapseChange?: (collapsed: boolean) => void;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	top?: number;
}

export const API_KEYS_PANEL_STORAGE_KEY = 'apiKeysPanel.width';
export const API_KEYS_PANEL_DEFAULT_WIDTH = 500;
export const API_KEYS_PANEL_MIN_WIDTH = 360;
export const API_KEYS_PANEL_MAX_WIDTH = 900;
export const API_KEYS_PANEL_COLLAPSED_WIDTH = 48;

export default function ApiKeysPanel({ onClose, onApiKeySelect, selectedApiKeyId, onCollapseChange, width, setWidth, top = 0 }: ApiKeysPanelProps) {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const { data, isLoading, isFetching, error, refetch } = useApiKeys();

	const apiKeys = useMemo(() => data || [], [data]);
	const columns: SidePanelListColumn[] = [
		{ id: 'name', label: 'Name', align: 'left' },
		{ id: 'code', label: 'Code', align: 'left' },
		{ id: 'dependencies', label: 'Deps', align: 'center', width: 72 },
		{ id: 'enabled', label: '', align: 'center', width: 76 },
	];

	const handleToggleCollapse = () => {
		const next = !isCollapsed;
		setIsCollapsed(next);
		onCollapseChange?.(next);
	};

	const renderCell = (item: ApiKeyModel, columnId: string) => {
		switch (columnId) {
			case 'name':
				return (
					<Stack direction="row" spacing={1} alignItems="center">
						<VpnKey sx={{ fontSize: 16, color: 'primary.main' }} />
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{item.name}
						</Typography>
					</Stack>
				);
			case 'code':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
						{item.code}
					</Typography>
				);
			case 'dependencies':
				return <Chip label={item.dependencies?.length || 0} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
			case 'enabled':
				return <Chip label={item.isEnabled === false ? 'Off' : 'On'} size="small" color={item.isEnabled === false ? 'default' : 'success'} variant="outlined" />;
			default:
				return null;
		}
	};

	return (
		<>
			<SidePanelList
				footer={<AccessCredentialHelp kind="apiKey" />}
				title="API Keys"
				subtitle="Shared keys for runtime access"
				items={apiKeys}
				columns={columns}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				emptyMessage="No API keys found"
				selectedItemId={selectedApiKeyId}
				width={width}
				collapsedWidth={API_KEYS_PANEL_COLLAPSED_WIDTH}
				isCollapsed={isCollapsed}
				onClose={onClose}
				onRefresh={() => refetch()}
				onItemSelect={(item) => onApiKeySelect(item.id)}
				renderCell={renderCell}
				getItemId={(item) => item.id}
				onToggleCollapse={handleToggleCollapse}
				resizeStorageKey={API_KEYS_PANEL_STORAGE_KEY}
				defaultWidth={API_KEYS_PANEL_DEFAULT_WIDTH}
				minWidth={API_KEYS_PANEL_MIN_WIDTH}
				maxWidth={API_KEYS_PANEL_MAX_WIDTH}
				setWidth={setWidth}
				top={top}
				headerActions={
					<IconButton size="small" onClick={() => setOpenCreateDialog(true)} title="Create API key">
						<AddCircle />
					</IconButton>
				}
			/>
			<CreateApiKeyDialog
				open={openCreateDialog}
				onClose={() => setOpenCreateDialog(false)}
				onCreated={(apiKeyId) => {
					setOpenCreateDialog(false);
					refetch();
					onApiKeySelect(apiKeyId);
				}}
			/>
		</>
	);
}
